import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Send,
    Mic,
    MicOff,
    Square,
    Loader2,
    MapPin,
    Clock,
    Zap,
    Target,
    Radio,
} from "lucide-react";
import { api } from "@/api";
import { CompactTaskCard } from "@/components/CompactTaskCard";
import { daysDiff, getMissedOccurrences } from "@/utils";
import { SafeLinkText } from "@/components/SafeLinkText";
import { AssistantAvatar } from "@/components/AssistantAvatar";
import { AperturePulseRing } from "@/components/AperturePulseRing";
import { speak, speakSequence, stopSpeaking, ttsSupported, unlockAudio, onVoiceBlocked, retryBlockedSpeech } from "@/lib/tts";

const iconFor = (name) => {
    switch ((name || "").toLowerCase()) {
        case "map-pin":
            return MapPin;
        case "clock":
            return Clock;
        case "zap":
            return Zap;
        case "target":
            return Target;
        default:
            return Radio;
    }
};

const toneRing = {
    red: "border-red-500/40 bg-red-500/10 text-red-300",
    fuchsia: "border-meye-fuchsia/40 bg-meye-fuchsia/10 text-meye-fuchsia",
    cyan: "border-meye-cyan/40 bg-meye-cyan/10 text-meye-cyan",
    violet: "border-violet-500/40 bg-violet-500/10 text-violet-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

const SR_CLASS = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

/**
 * Conversational AI co-pilot.
 * Voice mode uses a strict turn-based loop:
 *   listening → (silence) → thinking (mic off) → speaking (mic off) → listening
 * The mic is fully aborted during thinking + speaking so the AI's own voice
 * cannot feed back into the recognizer.
 */
export const MissionChat = ({
    settings,
    tasks,
    openHint,
    onHintConsumed,
    autoVoice = false,
    onToggleTask,
    onEditTask,
    onDataChanged,
    onSettingsChange,
    variant = "tab",
}) => {
    const [log, setLog] = useState([]);
    const [expandedCards, setExpandedCards] = useState(() => new Set()); // older-turn task lists opened by the user
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    // Voice mode is ON by default. If the user previously toggled it off, respect that.
    const [voiceOn, setVoiceOn] = useState(() => {
        if (autoVoice) return true;
        try {
            return localStorage.getItem("meye_voice_off") !== "1";
        } catch {
            return true;
        }
    });
    const [voiceState, setVoiceState] = useState("idle"); // idle | listening | thinking | speaking
    const [liveTranscript, setLiveTranscript] = useState("");
    const [audioBlocked, setAudioBlocked] = useState(false);

    const bottomRef = useRef(null);
    const logContainerRef = useRef(null);
    const recorderRef = useRef(null); // MediaRecorder for PTT
    const chunksRef = useRef([]);
    const recRef = useRef(null); // SpeechRecognition
    const finalBufRef = useRef("");
    // Index of the first result in the CURRENT recognition session that has NOT yet been sent
    // to the LLM. On Android Chrome, `continuous = true` frequently re-fires `onresult` with
    // ALL prior finalized results (including the ones we just consumed via doSend) — so we
    // must skip anything <= this index or we get the "just just just describe describe..."
    // duplication chain the user was seeing. iOS Safari doesn't do this, but the guard is a
    // no-op there. Resets to 0 whenever `onstart` fires (fresh session, fresh results array).
    const consumedResultsRef = useRef(0);
    const silenceTimerRef = useRef(null);
    const busyRef = useRef(false); // true while thinking (mic stopped) — NOT during speaking (mic stays on for barge-in)
    const speakingRef = useRef(false); // true while Nova is speaking; barge-in eligible
    const speakingWordsRef = useRef(new Set()); // token set of Nova's current speech — echo filter
    const bargeAbortRef = useRef(false); // set true when user interrupts mid-speech
    const wantsRecRef = useRef(false); // reflects voiceOn without stale closures
    const openGreetedRef = useRef(false);
    const logRef = useRef([]); // parallel to `log` for fresh reads inside async handlers

    const greetOnOpen = settings?.comms_greet_on_open !== false;
    const ttsOn = !!settings?.comms_tts_enabled;
    // Voice speaks whenever hands-free voice mode is on OR the legacy tts flag is on.
    const voiceCanSpeak = voiceOn || ttsOn;
    const ttsVoice = settings?.comms_tts_voice || "nova";

    const push = (msg) => {
        setLog((prev) => {
            const next = [...prev, { ...msg, ts: Date.now() }];
            logRef.current = next;
            return next;
        });
    };

    const scrollToBottom = () => {
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 60);
    };

    // Anchor scroll so that after a Send the user's outbound message lands at the very TOP of
    // the log's visible area — the AI's reply then flows in right below it, no truncation.
    // Uses getBoundingClientRect so the maths is correct regardless of where offsetParent
    // sits in the DOM (our log container is NOT position:relative, so el.offsetTop measured
    // against the wrong ancestor before — that's why the "scroll up to neural link" broke).
    //
    // We deliberately DO NOT auto-clear the anchor idx here: the reply can arrive several
    // seconds later and we need the anchor to still be valid so the post-reply scroll pass
    // can reach the target (max scroll only grows enough once the reply text expands the
    // log's scrollHeight). Clearing is done in doSend's finally.
    const scrollAnchorIdxRef = useRef(null);
    const scrollAnchorToTop = () => {
        const doScroll = () => {
            const idx = scrollAnchorIdxRef.current;
            const container = logContainerRef.current;
            if (idx == null || !container) return;
            const el = container.querySelector(`[data-msg-index="${idx}"]`);
            if (!el) return;
            const cRect = container.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            const target = container.scrollTop + (eRect.top - cRect.top);
            container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
        };
        // Three passes catches (a) the moment we push the user message, (b) after the reply
        // text expands the log's scrollHeight, and (c) a final settle once the smooth-scroll
        // animation is done. Without (b)+(c) the browser caps scrollTop at the pre-reply
        // maxScroll and the user's message never actually reaches the top.
        requestAnimationFrame(doScroll);
        setTimeout(doScroll, 120);
        setTimeout(doScroll, 400);
    };

    // ---- Speech recognition lifecycle ----------------------------------
    const stopRec = () => {
        // Take the ref before nulling so we can still call methods on the old recognizer.
        // Order matters: (1) mark that we don't want restarts (wantsRecRef=false is set by the
        // caller's useEffect but we belt-and-suspenders it here), (2) null the ref so the
        // onend restart-guard trips, (3) DETACH the callbacks so even a delayed onend can't
        // ressurect the mic, (4) call both stop() AND abort() — Chrome + Safari need
        // different ones and calling both is safe.
        const rec = recRef.current;
        recRef.current = null;
        wantsRecRef.current = false;
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        finalBufRef.current = "";
        setLiveTranscript("");
        if (rec) {
            try { rec.onresult = null; } catch { /* ignore */ }
            try { rec.onend = null; } catch { /* ignore */ }
            try { rec.onerror = null; } catch { /* ignore */ }
            try { rec.onstart = null; } catch { /* ignore */ }
            try { rec.stop(); } catch { /* ignore */ }
            try { rec.abort(); } catch { /* ignore */ }
        }
    };

    const startRec = () => {
        if (recRef.current || busyRef.current || !wantsRecRef.current) return;
        if (!SR_CLASS) {
            toast.error("Voice mode needs Chrome or Edge");
            setVoiceOn(false);
            return;
        }
        const rec = new SR_CLASS();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = "en-US";

        rec.onresult = (evt) => {
            // Never process while the LLM is thinking (mic shouldn't even be on then)
            if (busyRef.current) return;
            // Rebuild finals+interim FROM SCRATCH every event, honoring the consumed cursor.
            // - Anything at index < consumedResultsRef.current has already been sent → skip.
            // - Anything else that is `isFinal` goes into `final`, others into `interim`.
            // This makes the loop idempotent under Android Chrome's habit of re-dispatching the
            // same finals repeatedly (which caused the runaway "just just just describe describe…"
            // duplication chain on production).
            let final = "";
            let interim = "";
            for (let i = 0; i < evt.results.length; i++) {
                if (i < consumedResultsRef.current) continue;
                const r = evt.results[i];
                if (r.isFinal) final += r[0].transcript + " ";
                else interim += r[0].transcript;
            }
            finalBufRef.current = final;
            const combined = (final + interim).trim();
            setLiveTranscript(combined);

            // BARGE-IN: while Nova speaks, decide if this is (a) her voice echoing back
            // or (b) the user actually interrupting. Compute word overlap with what she's saying.
            if (speakingRef.current) {
                const tokens = combined.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
                if (tokens.length < 2) return; // need at least 2 real words to trigger
                const novaWords = speakingWordsRef.current;
                const echoHits = tokens.filter((w) => novaWords.has(w)).length;
                const overlap = echoHits / tokens.length;
                if (overlap > 0.6) return; // most words are Nova's — treat as echo
                // Real barge-in. Cut Nova off + treat as new user input.
                bargeAbortRef.current = true;
                stopSpeaking();
                speakingRef.current = false;
                if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
                silenceTimerRef.current = setTimeout(() => {
                    const toSend = finalBufRef.current.trim();
                    if (toSend.length >= 2) {
                        // Mark all currently-observed results as consumed so re-fires (Android)
                        // don't leak the same text into the next utterance.
                        try { consumedResultsRef.current = evt.results.length; } catch { /* ignore */ }
                        finalBufRef.current = "";
                        setLiveTranscript("");
                        doSend(toSend);
                    }
                }, 700);
                return;
            }

            if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = setTimeout(() => {
                const toSend = finalBufRef.current.trim();
                if (toSend.length >= 2 && !busyRef.current) {
                    try { consumedResultsRef.current = evt.results.length; } catch { /* ignore */ }
                    finalBufRef.current = "";
                    setLiveTranscript("");
                    doSend(toSend);
                }
            }, 1200);
        };
        rec.onerror = (e) => {
            if (e.error === "not-allowed") {
                toast.error("Mic access denied");
                setVoiceOn(false);
            }
        };
        rec.onstart = () => {
            // Fresh session → fresh evt.results array → reset the consumed cursor.
            consumedResultsRef.current = 0;
        };
        rec.onend = () => {
            // Auto-restart if we still want the mic open and we're not thinking.
            // We deliberately allow restart while speakingRef=true so barge-in stays live.
            if (recRef.current === rec && wantsRecRef.current && !busyRef.current) {
                try { rec.start(); } catch { /* already started */ }
            }
        };
        try { rec.start(); } catch { /* ignore */ }
        recRef.current = rec;
        setVoiceState("listening");
    };

    // Local commands that bypass the LLM (fast, deterministic).
    const jsonInputRef = useRef(null);

    const doExport = async () => {
        try {
            const bundle = await api.exportBackup();
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `meyetasks-backup-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
            push({ role: "assistant", text: "// backup exported to your downloads." });
        } catch (e) {
            console.error(e);
            toast.error("Export failed");
            push({ role: "assistant", text: "// export failed. try again?" });
        }
    };

    const onJsonPicked = async (e) => {
        const file = e.target.files?.[0];
        if (jsonInputRef.current) jsonInputRef.current.value = "";
        if (!file) return;
        try {
            const raw = await file.text();
            const parsed = JSON.parse(raw);
            const res = await api.importBackup(parsed); // dry run only — apply lives in Tools → Backup
            const s = res.tasks?.summary || {};
            toast.message(`Import check: ${s.safe || 0} safe · ${s.canonicalized || 0} canonicalized · ${s.blocked || 0} blocked`);
            push({ role: "assistant", text: `// import dry run: ${s.safe || 0} safe, ${s.canonicalized || 0} canonicalized, ${s.blocked || 0} blocked. nothing written — open Tools → Backup → Import to review and Apply.` });
            onDataChanged?.();
        } catch (err) {
            console.error(err);
            toast.error(err?.message?.includes("JSON") ? "Not a valid JSON file" : "Import failed");
            push({ role: "assistant", text: "// import failed. check the file." });
        }
    };

    // ---- Main send function (used by voice AND text) -------------------
    const doSend = async (bodyText, kind = "user") => {
        if (!bodyText && kind !== "on_open") return;

        // ---- Local commands (bypass Nova) ----
        const cmd = bodyText.trim().toLowerCase();
        if (cmd === "export" || cmd === "export backup" || cmd === "backup") {
            push({ role: "user", text: bodyText });
            await doExport();
            return;
        }
        if (cmd === "import" || cmd === "import backup" || cmd === "restore") {
            push({ role: "user", text: bodyText });
            jsonInputRef.current?.click();
            return;
        }

        // Fully pause mic during THINKING only. Mic will restart before speaking begins so user can barge in.
        busyRef.current = true;
        speakingRef.current = false;
        bargeAbortRef.current = false;
        stopRec();
        stopSpeaking();
        setSending(true);
        if (bodyText && kind !== "on_open") {
            // Capture the log length AFTER pushing so the anchor points at the user message we
            // just added. When the reply comes back we scroll THIS node to the top of the pane.
            push({ role: "user", text: bodyText });
            scrollAnchorIdxRef.current = logRef.current.length - 1;
            // Snap the user's message to the top of the visible log RIGHT NOW so the reply
            // lands in the visible area (not below the fold requiring a manual scroll up).
            scrollAnchorToTop();
        }
        setVoiceState(voiceOn ? "thinking" : "idle");

        try {
            const requestId = api.newRequestId(); // minted once per logical submit (voice or typed)
            const res = await api.aiMission(bodyText || "", logRef.current.slice(-8), kind, false, requestId);
            const replyText = res.reply || "";
            push({
                role: "assistant",
                text: replyText,
                display: res.display || [],
                training_question: res.training_question,
            });
            if (res.created?.length) onDataChanged?.();
            // Snap the user-message anchor to the top of the pane so the reply lands right
            // below it. We intentionally DO NOT call scrollToBottom() here — it used to race
            // with scrollAnchorToTop and produced the "3 dots then disappears" flicker.
            scrollAnchorToTop();

            // Speak reply — sentence-pipelined so audio starts within ~500ms of first sentence being ready.
            const shouldSpeak = replyText && ttsSupported() && (voiceOn || ttsOn);
            if (shouldSpeak) {
                if (voiceOn) setVoiceState("speaking");
                // Prime echo filter with all words Nova is about to say
                speakingWordsRef.current = new Set(
                    replyText.toLowerCase().replace(/[^\w\s]/g, " ").split(/\s+/).filter((w) => w.length > 2)
                );
                speakingRef.current = true;
                // Restart mic BEFORE audio starts so barge-in is live from sentence #1.
                if (voiceOn) {
                    busyRef.current = false;
                    startRec();
                }
                await speakSequence(replyText, {
                    voiceName: ttsVoice,
                    shouldAbort: () => bargeAbortRef.current,
                });
                speakingRef.current = false;
                speakingWordsRef.current = new Set();
            }
        } catch (err) {
            console.error(err);
            push({ role: "assistant", text: "// signal lost. try again?" });
            toast.error("Comms error");
        } finally {
            setSending(false);
            busyRef.current = false;
            // NB: we intentionally do NOT clear scrollAnchorIdxRef here — the finally block
            // fires synchronously right after `scrollAnchorToTop()` when voice is off, which
            // would nuke the anchor before the RAF/120ms/400ms scroll passes get to run.
            // The next send overwrites the ref anyway, so leaving it alive is harmless.
            if (wantsRecRef.current) {
                setVoiceState("listening");
                startRec();
            } else {
                setVoiceState("idle");
            }
        }
    };

    // ---- Voice-mode toggle effect --------------------------------------
    useEffect(() => {
        wantsRecRef.current = voiceOn;
        if (voiceOn) {
            unlockAudio(); // guaranteed user gesture (this ran from the click that set voiceOn)
            if (!busyRef.current) startRec();
        } else {
            stopRec();
            setVoiceState("idle");
        }
    }, [voiceOn]);

    // Cleanup on unmount
    useEffect(() => () => {
        stopRec();
        stopSpeaking();
    }, []);

    // Track when TTS gets blocked by autoplay so we can surface a persistent UNMUTE button.
    useEffect(() => {
        return onVoiceBlocked(() => setAudioBlocked(true));
    }, []);

    const unmuteVoice = async () => {
        await unlockAudio();
        await retryBlockedSpeech();
        setAudioBlocked(false);
    };

    // ---- Proactive greet on open --------------------------------------
    useEffect(() => {
        if (!greetOnOpen || openGreetedRef.current) return;
        openGreetedRef.current = true;
        const hint = openHint || "";
        // If we arrived via the screensaver COMMS button, tell the AI to keep it VERY short
        // so we don't monopolize the mic before the user says anything.
        doSend(hint, "on_open");
        if (hint) onHintConsumed?.();
    }, [greetOnOpen]);

    // ---- Text submit --------------------------------------------------
    const submit = (e) => {
        e?.preventDefault();
        const q = text.trim();
        if (!q || sending) return;
        setText("");
        doSend(q);
    };

    // ---- Push-to-talk (Whisper on backend, one-shot) ------------------
    const startPtt = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setTranscribing(true);
                try {
                    const { text: transcript } = await api.transcribe(blob);
                    if (transcript?.trim()) doSend(transcript.trim());
                    else toast.warning("Nothing transcribed");
                } catch {
                    toast.error("Transcribe failed");
                } finally {
                    setTranscribing(false);
                }
            };
            rec.start();
            recorderRef.current = rec;
        } catch {
            toast.error("Mic access denied");
        }
    };
    const stopPtt = () => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    };

    // ---- Display helpers ----------------------------------------------
    // Live-filter a task_list card so history never shows stale state: completed / archived
    // one-shots drop out, and an "Overdue" card only keeps tasks that are STILL overdue right now
    // (a checked-in dose or a beat due tomorrow must not sit in Overdue). Read-only — the task
    // engine is untouched; this is purely which existing rows the chat chooses to render.
    const liveItems = (d) => {
        const title = (d.title || "").toLowerCase();
        const overdueCard = title.includes("overdue");
        const seen = new Set();
        return (d.task_ids || [])
            .map((id) => tasks.find((t) => t.id === id))
            .filter(Boolean)
            .filter((t) => !seen.has(t.id) && seen.add(t.id))
            .filter((t) => !t.archived)
            .filter((t) => t.type === "recurring" || !t.completed)
            .filter((t) => {
                if (!overdueCard) return true;
                if (t.type === "recurring" && t.interval) return getMissedOccurrences(t).length > 0;
                const dd = t.date ? daysDiff(t.date) : null;
                return dd !== null && dd < 0;
            })
            .map((t) => (t.type === "recurring" && t.interval && overdueCard
                ? (() => { const m = getMissedOccurrences(t); return { ...t, _missedOccurrences: m, _missedCount: m.length, _oldestMissedIso: m[0] }; })()
                : t));
    };

    const renderDisplay = (d, i, { isLatest = true } = {}) => {
        if (d.type === "task_list") {
            const items = liveItems(d);
            if (!items.length) return null;
            const tone = toneRing[d.tone] || toneRing.fuchsia;
            const overdueCard = (d.title || "").toLowerCase().includes("overdue");
            const open = isLatest || expandedCards.has(i);
            return (
                <div key={i} className="mt-2" data-testid={`mission-tasklist-${i}`}>
                    <button
                        type="button"
                        onClick={() => setExpandedCards((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                        className={`inline-flex items-center gap-1.5 text-[10px] font-brand font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${tone} mb-1.5`}
                        data-testid={`mission-tasklist-toggle-${i}`}
                    >
                        {d.title || "tasks"} · {items.length}
                        {!isLatest && <span className="text-[9px] opacity-70">{open ? "▾" : "▸"}</span>}
                    </button>
                    {open && (
                        <div className="space-y-1.5">
                            {items.map((t) => (
                                <CompactTaskCard
                                    key={t.id}
                                    task={t}
                                    dense
                                    occurrenceIso={t.type === "recurring" && overdueCard && t._missedOccurrences?.length === 1 ? t._missedOccurrences[0] : undefined}
                                    onToggle={onToggleTask}
                                    onEdit={onEditTask}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        if (d.type === "quick_replies") {
            const opts = d.options || [];
            if (!opts.length) return null;
            return (
                <div key={i} className="mt-3 flex flex-wrap gap-2" data-testid={`mission-quickreplies-${i}`}>
                    {opts.map((o) => (
                        <button
                            key={o}
                            onClick={() => doSend(o)}
                            disabled={sending}
                            className="text-xs font-brand font-medium px-3 py-1.5 rounded-full border border-meye-cyan/40 bg-meye-cyan/10 text-meye-cyan hover:bg-meye-cyan/20 transition-colors"
                            data-testid={`mission-quickreply-${o.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {o}
                        </button>
                    ))}
                </div>
            );
        }
        if (d.type === "context_banner") {
            const Icon = iconFor(d.icon);
            const tone = toneRing[d.tone] || toneRing.cyan;
            return (
                <div
                    key={i}
                    className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 ${tone} max-w-full min-w-0`}
                    data-testid={`mission-banner-${i}`}
                >
                    <Icon size={14} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-brand font-medium min-w-0 flex-1" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}><SafeLinkText text={d.text} /></span>
                </div>
            );
        }
        return null;
    };

    const lastAssistantIdx = (() => { for (let k = log.length - 1; k >= 0; k--) if (log[k].role !== "user") return k; return -1; })();

    // ---- Voice state label -------------------------------------------
    const stateMeta = {
        idle:      { label: "voice off",   dot: "bg-zinc-600",         tint: "text-zinc-500" },
        listening: { label: "listening",   dot: "bg-meye-cyan",        tint: "text-meye-cyan" },
        thinking:  { label: "thinking…",   dot: "bg-violet-400",       tint: "text-violet-300" },
        speaking:  { label: "speaking",    dot: "bg-meye-fuchsia",     tint: "text-meye-fuchsia" },
    }[voiceState];

    return (
        <div
            className={
                variant === "rail"
                    ? "fade-up flex flex-col h-full min-h-0 min-w-0"
                    : "fade-up flex flex-col h-[calc(100vh-320px)] min-h-[400px] min-w-0"
            }
            data-testid="mission-chat"
        >
            {/* Hidden JSON file picker for `import` local command */}
            <input
                ref={jsonInputRef}
                type="file"
                accept="application/json,.json"
                onChange={onJsonPicked}
                className="hidden"
                data-testid="mission-import-file"
            />
            {/* Status strip */}
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-mono mb-2">
                <span className="flex items-center gap-2 text-zinc-400">
                    <AperturePulseRing state={voiceOn ? voiceState : "idle"} size={22}>
                        <AssistantAvatar id={settings?.assistant_avatar} size={22} />
                    </AperturePulseRing>
                    <span className="font-brand font-bold text-meye-fuchsia normal-case tracking-normal text-xs">
                        {settings?.assistant_name || "mEYEtrainer"}
                    </span>
                    <span className="text-zinc-600">·</span>
                    <span>let&apos;s chat</span>
                </span>
                <div className={`flex items-center gap-2 ${stateMeta.tint}`}>
                    {audioBlocked && voiceCanSpeak && (
                        <button
                            type="button"
                            onClick={unmuteVoice}
                            className="flex items-center gap-1 text-[10px] font-brand font-bold uppercase tracking-widest px-2 py-1 rounded-full border border-amber-400/60 bg-amber-400/15 text-amber-300 hover:bg-amber-400/25 animate-pulse"
                            data-testid="mission-unmute-btn"
                            aria-label="Unmute trainer voice"
                            title="Browser blocked audio — tap to enable"
                        >
                            🔊 unmute
                        </button>
                    )}
                    <span className={`w-1.5 h-1.5 rounded-full ${stateMeta.dot} ${voiceOn ? "animate-pulse" : ""}`} />
                    <span>{stateMeta.label}</span>
                    <button
                        type="button"
                        onClick={() => {
                            // Synchronous unlock inside the gesture — iOS Safari needs this
                            unlockAudio();
                            setVoiceOn((v) => {
                                const next = !v;
                                try {
                                    if (next) localStorage.removeItem("meye_voice_off");
                                    else localStorage.setItem("meye_voice_off", "1");
                                } catch { /* ignore */ }
                                return next;
                            });
                        }}
                        className={`ml-1 p-1.5 rounded-full border transition-colors ${
                            voiceOn
                                ? "border-meye-cyan/60 bg-meye-cyan/15 text-meye-cyan"
                                : "border-meye-border bg-black/40 text-zinc-400 hover:text-meye-cyan"
                        }`}
                        data-testid="mission-voice-toggle"
                        aria-label={voiceOn ? "Turn voice off" : "Turn voice on"}
                        title={voiceOn ? "Voice mode: on (click to disable)" : "Enable hands-free voice"}
                    >
                        {voiceOn ? <Mic size={13} /> : <MicOff size={13} />}
                    </button>
                </div>
            </div>

            {/* Live transcript preview while listening */}
            {voiceOn && liveTranscript && voiceState === "listening" && (
                <div
                    className="mb-2 rounded-md border border-meye-cyan/30 bg-meye-cyan/5 px-3 py-1.5 text-xs text-meye-cyan/90 font-mono italic"
                    data-testid="mission-live-transcript"
                >
                    &ldquo;{liveTranscript}&rdquo;
                </div>
            )}

            {/* Prominent one-tap voice unlock banner — appears when browser has blocked TTS */}
            {audioBlocked && voiceCanSpeak && (
                <button
                    type="button"
                    onClick={unmuteVoice}
                    className="mb-3 w-full rounded-lg border border-amber-400/50 bg-gradient-to-r from-amber-500/20 to-amber-400/10 px-4 py-3 flex items-center gap-3 hover:from-amber-500/30 hover:to-amber-400/15 transition-colors group"
                    data-testid="mission-unmute-banner"
                >
                    <span className="text-2xl animate-pulse">🔊</span>
                    <div className="flex-1 text-left">
                        <p className="font-brand text-sm font-bold text-amber-200 uppercase tracking-widest">
                            Tap to enable Nova&apos;s voice
                        </p>
                        <p className="text-[10px] text-amber-300/80 font-mono mt-0.5">
                            Your browser blocked audio autoplay — one tap unlocks it forever
                        </p>
                    </div>
                    <span className="text-[10px] font-brand font-bold uppercase tracking-widest text-amber-200 bg-amber-500/20 rounded-full px-2 py-1 group-hover:bg-amber-500/30">
                        Enable
                    </span>
                </button>
            )}

            {/* Log — min-w-0 + overflow-x-hidden is critical: without them the chat panel grows to
                the widest AI reply and the text spills off the right edge of the desktop rail. */}
            <div ref={logContainerRef} className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden space-y-4 pr-1 pb-3" data-testid="mission-log">
                {log.length === 0 && (
                    <div className="text-center text-zinc-600 text-xs font-mono py-10">
                        // establishing link…
                    </div>
                )}
                {log.map((m, i) => (
                    <div key={i} className="min-w-0" data-msg-index={i} data-testid={`mission-msg-${m.role === "user" ? "user" : "assistant"}-${i}`}>
                        {m.role === "user" ? (
                            <div className="flex justify-end min-w-0">
                                <div className="max-w-[85%] min-w-0 bg-meye-fuchsia/15 border border-meye-fuchsia/25 rounded-2xl rounded-br-md px-4 py-2 text-sm text-zinc-100 whitespace-pre-wrap" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                    <SafeLinkText text={m.text} />
                                </div>
                            </div>
                        ) : (
                            <div className="flex justify-start min-w-0 w-full">
                                {/* Assistant bubble — `flex-1` REMOVED (was forcing the bubble to
                                    fill 92% of the row even for short replies, and can push past
                                    the parent's min-w bound). `w-full + max-w-[92%]` bounds it to
                                    the row width while still capping at 92% of the row. Inner
                                    div is now `min-w-0 overflow-hidden` so any stray unbroken
                                    string cannot spill past the bubble border. */}
                                <div className="max-w-[92%] min-w-0 w-full">
                                    <div className="bg-meye-card border border-meye-border rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-zinc-100 whitespace-pre-wrap min-w-0" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                        <SafeLinkText text={m.text} />
                                    </div>
                                    {(() => {
                                        // Cards are CHILDREN of this Don response: rendered on a rail under the
                                        // bubble, deduped by title within the turn, collapsed for older turns.
                                        const isLatest = i === lastAssistantIdx;
                                        const seenTitles = new Set();
                                        const blocks = (m.display || [])
                                            .filter((d) => {
                                                if (d?.type !== "task_list") return true;
                                                const k = (d.title || "").toLowerCase();
                                                if (seenTitles.has(k)) return false;
                                                seenTitles.add(k); return true;
                                            })
                                            .map((d, j) => renderDisplay(d, `${i}-${j}`, { isLatest }))
                                            .filter(Boolean);
                                        return blocks.length ? (
                                            <div className="ml-3 pl-3 border-l-2 border-meye-border/70 min-w-0" data-testid={`mission-response-cards-${i}`}>
                                                {blocks}
                                            </div>
                                        ) : null;
                                    })()}
                                    {m.training_question && (
                                        <div className="mt-3 text-xs font-brand text-meye-fuchsia border border-meye-fuchsia/40 bg-meye-fuchsia/10 rounded-2xl px-3 py-1.5 min-w-0 whitespace-pre-wrap" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                            ? {m.training_question}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {sending && (
                    <div className="flex justify-start" data-testid="mission-typing">
                        <div className="bg-meye-card border border-meye-border rounded-2xl px-4 py-2.5">
                            <div className="flex gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-meye-fuchsia animate-pulse" />
                                <span className="w-1.5 h-1.5 rounded-full bg-meye-fuchsia animate-pulse" style={{ animationDelay: "0.15s" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-meye-fuchsia animate-pulse" style={{ animationDelay: "0.3s" }} />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form onSubmit={submit} className="flex items-center gap-2 pt-2 border-t border-meye-border/60">
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={
                        voiceOn
                            ? "or type instead…"
                            : sending ? "thinking…" : "talk to your trainer…"
                    }
                    disabled={sending}
                    className="flex-1 bg-black/60 border border-meye-border rounded-lg px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-meye-cyan/60 focus:ring-1 focus:ring-meye-cyan/20 font-mono"
                    data-testid="mission-input"
                />
                {/* One-shot push-to-talk mic (only when voice mode is OFF) */}
                {!voiceOn && (
                    transcribing ? (
                        <button type="button" className="p-2.5 rounded-full bg-meye-cyan/20 text-meye-cyan" disabled>
                            <Loader2 size={14} className="animate-spin" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onMouseDown={startPtt}
                            onMouseUp={stopPtt}
                            onTouchStart={startPtt}
                            onTouchEnd={stopPtt}
                            disabled={sending}
                            className="p-2.5 rounded-full border border-meye-fuchsia/50 text-meye-fuchsia hover:bg-meye-fuchsia/10"
                            data-testid="mission-ptt-btn"
                            title="Hold to talk"
                        >
                            <Mic size={14} />
                        </button>
                    )
                )}
                <button
                    type="submit"
                    disabled={!text.trim() || sending}
                    className="p-2.5 rounded-full bg-meye-cyan text-black hover:bg-cyan-300 disabled:opacity-40"
                    data-testid="mission-send-btn"
                >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2} />}
                </button>
            </form>
        </div>
    );
};

export default MissionChat;
